import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useAlertas, useEnviarAlertas } from './api';

/**
 * Alertas — lo que vence y lo que ya venció, en todos los recursos.
 *
 * Era un `Utils.modal` de 720 px con una tabla dentro; aquí es un panel, que
 * cabe en el móvil. Y sus filas ya no se pintan sin escapar: el nombre del
 * registro y el del campo los escribe quien configura el recurso.
 */
export function Alertas({ onCerrar }: { onCerrar: () => void }) {
  const consulta = useAlertas(true);
  const enviar = useEnviarAlertas();
  const d = consulta.data;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Alertas de vencimiento"
      descripcion="Los campos de tipo vencimiento o estado, en todos los recursos."
      ancho="lg"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button
            loading={enviar.isPending}
            disabled={!d?.total}
            razonDeshabilitado="No hay nada que resumir"
            onClick={() => enviar.mutate(undefined, {
              onSuccess: (r) => Utils.toast(r.message, 'success'),
              onError: (err) => Utils.toast(
                err instanceof Error ? err.message : 'No se pudo enviar', 'error'),
            })}
          >
            <i className="bi bi-envelope" aria-hidden="true" /> Enviarme el resumen
          </Button>
        </>
      }
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="las alertas"
          onReintentar={() => void consulta.refetch()}
        />
      ) : !d?.total ? (
        <EstadoVacio
          icono="bi-check-circle"
          titulo="Todo al día"
          descripcion="No hay documentos próximos a vencer ni vencidos."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2">
            <p className="rounded-sm border border-danger/40 bg-danger/12 p-3 text-center">
              <strong className="block text-7 font-extrabold leading-none tabular-nums text-danger-ink">
                {d.vencidos}
              </strong>
              <span className="mt-1 block text-2 text-ink-2">Vencidos</span>
            </p>
            <p className="rounded-sm border border-warn/40 bg-warn/16 p-3 text-center">
              <strong className="block text-7 font-extrabold leading-none tabular-nums text-warn-ink">
                {d.por_vencer}
              </strong>
              <span className="mt-1 block text-2 text-ink-2">Próximos a vencer</span>
            </p>
          </div>

          <div className="max-h-[55vh] overflow-auto rounded-sm border border-line">
            <table className="w-full border-collapse text-2">
              <thead className="sticky top-0 bg-sunken">
                <tr className="border-b border-line text-left text-muted">
                  {['Recurso', 'Registro', 'Campo', 'Estado', 'Fecha'].map((h) => (
                    <th key={h} scope="col" className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.items.map((a, i) => (
                  <tr key={i} className="border-b border-line last:border-0">
                    <td className="px-3 py-1.5">
                      {/* La alerta lleva a su recurso, que ahora tiene dirección. */}
                      <a
                        href={`#recursos-privados/recurso/${a.resource_id}`}
                        className="text-brand-ink hover:underline focus-visible:focus-anillo"
                        onClick={onCerrar}
                      >
                        {a.resource}
                      </a>
                    </td>
                    <td className="px-3 py-1.5 text-ink">{a.name}</td>
                    <td className="px-3 py-1.5 text-ink-2">{a.field}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={a.status === 'Vencido' ? 'danger' : 'warn'}>{a.status}</Badge>
                    </td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-ink-2">{a.date || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Drawer>
  );
}
